import { ref, computed } from 'vue'
import { defineStore } from 'pinia'

/**
 * RED-180 — selection state for the standalone broker .docx export
 * reachable from the Tehingute kaart.
 *
 * This store is intentionally isolated from `useTransactionStore`'s
 * `markedTransactions` (the Hindamine evaluation selection). The broker
 * feature lives only on `/transactions` without an `objectId` and must
 * never touch the Hindamine flow's state — Toomas's RED-180 review
 * 2026-05-27 confirmed the two are unrelated user journeys.
 *
 * Selection is ephemeral by design: it lives in this Pinia store for the
 * lifetime of the SPA session and is lost on page reload. No localStorage,
 * no server persistence — Siim's RED-180 decision so it doesn't require a
 * migration or a per-user state column.
 */
export const MAX_BROKER_SELECTION = 5

export const useBrokerSelectionStore = defineStore('brokerSelection', () => {
  const selectedIds = ref([])

  const count = computed(() => selectedIds.value.length)
  const isAtLimit = computed(() => selectedIds.value.length >= MAX_BROKER_SELECTION)

  const has = id => selectedIds.value.includes(id)

  /**
   * Add a transaction to the broker selection.
   * @returns {boolean} true if added, false if rejected (already in or at limit)
   */
  const add = id => {
    if (selectedIds.value.includes(id)) return false
    if (selectedIds.value.length >= MAX_BROKER_SELECTION) return false
    selectedIds.value.push(id)
    return true
  }

  const remove = id => {
    const idx = selectedIds.value.indexOf(id)
    if (idx !== -1) selectedIds.value.splice(idx, 1)
  }

  /**
   * @returns {boolean} true if the toggle succeeded; false only when trying
   * to add past the cap.
   */
  const toggle = id => {
    if (has(id)) {
      remove(id)
      return true
    }
    return add(id)
  }

  const clear = () => {
    selectedIds.value = []
  }

  return { selectedIds, count, isAtLimit, has, add, remove, toggle, clear }
})
